import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '@/api/http';
import { Card, CardContent } from '@/components/ui/card';
import { PublicHeader } from '@/components/public/public-header';
import { PublicFooter } from '@/components/public/public-footer';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/pagamento/$paymentId')({
  validateSearch: (s: Record<string, unknown>) => ({
    status: typeof s.status === 'string' ? s.status : undefined,
  }),
  component: PagamentoPage,
});

const FREQ_LABEL: Record<string, string> = { WEEKLY: 'Semanal', MONTHLY: 'Mensal' };
const MODALITY_LABEL: Record<string, string> = {
  ONLINE: 'Online', PRESENCIAL: 'Presencial', HYBRID: 'Híbrido',
};

const money = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function PagamentoPage() {
  const { paymentId } = Route.useParams();
  const search = Route.useSearch() as { status?: string };
  const qc = useQueryClient();

  const [mode, setMode] = useState<'idle' | 'pix'>('idle');
  const [copied, setCopied] = useState(false);

  const paymentQ = useQuery({
    queryKey: ['payment', paymentId],
    queryFn: () => api.get(`payments/${paymentId}/public`).json<any>(),
    refetchInterval: mode === 'pix' ? 5000 : false,
  });

  const cardMut = useMutation({
    mutationFn: () => api.post(`payments/${paymentId}/checkout/card`).json<{ url: string | null }>(),
    onSuccess: (data) => { if (data.url) window.location.href = data.url; },
  });

  const pixMut = useMutation({
    mutationFn: () => api.post(`payments/${paymentId}/checkout/pix`).json<any>(),
    onSuccess: () => {
      setMode('pix');
      qc.invalidateQueries({ queryKey: ['payment', paymentId] });
    },
  });

  useEffect(() => {
    if (search.status === 'success') paymentQ.refetch();
  }, [search.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const p = paymentQ.data;
  const isPaid = p?.status === 'PAID';

  return (
    <div className="bg-[#FBF8F3] min-h-screen flex flex-col">
      <PublicHeader />
      <main className="flex-1 max-w-lg mx-auto px-4 py-12 w-full">
        {paymentQ.isLoading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <p className="text-sm text-slate-400">Carregando…</p>
          </div>
        ) : paymentQ.isError || !p ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-100">
            <p className="text-slate-500">Link de pagamento inválido ou expirado.</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <div className="inline-flex size-16 rounded-full bg-teal-50 items-center justify-center text-3xl mb-4">
                {isPaid ? '✅' : '💳'}
              </div>
              <h1 className="text-2xl font-extrabold text-slate-900">
                {isPaid ? 'Pagamento confirmado!' : 'Pagamento de sessões'}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {p.schedule
                  ? <>{p.schedule.totalSessions} sessões com <strong>{p.schedule.therapistName}</strong></>
                  : p.session
                  ? <>Sessão com <strong>{p.session.therapistName}</strong></>
                  : 'Pagamento'}
              </p>
            </div>

            <Card className="mb-6">
              <CardContent className="p-5 space-y-3">
                {p.schedule ? (
                  <>
                    <Row label="Sessões" value={`${p.schedule.totalSessions} sessões`} />
                    <Row label="Frequência" value={FREQ_LABEL[p.schedule.frequency] ?? p.schedule.frequency} />
                    <Row label="Duração" value={`${p.schedule.durationMin} min`} />
                    <Row label="Modalidade" value={MODALITY_LABEL[p.schedule.modality] ?? p.schedule.modality} />
                  </>
                ) : p.session ? (
                  <>
                    <Row label="Data" value={new Date(p.session.scheduledAt).toLocaleString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })} />
                    <Row label="Duração" value={`${p.session.durationMin} min`} />
                    <Row label="Modalidade" value={MODALITY_LABEL[p.session.modality] ?? p.session.modality} />
                  </>
                ) : null}
                <div className="border-t border-slate-100 pt-3">
                  <Row label="Total" value={money(p.amount)} bold />
                </div>
                {isPaid && p.paidAt && (
                  <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2.5 border border-emerald-200">
                    <span>✓</span>
                    <span>
                      Pago em {new Date(p.paidAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {!isPaid && (
              <div className="space-y-4">
                {mode === 'pix' && (p.pixQrCode || pixMut.data) ? (
                  <Card className="border-teal-200">
                    <CardContent className="p-5 space-y-4">
                      <p className="font-semibold text-slate-800 text-center">PIX — Copia e Cola</p>

                      {(p.pixQrCode || pixMut.data?.qrCodeImage) && (
                        <img
                          src={p.pixQrCode ?? pixMut.data?.qrCodeImage ?? ''}
                          alt="QR Code PIX"
                          className="mx-auto w-48 h-48 rounded-lg border border-slate-100"
                        />
                      )}

                      {(p.pixBrCode || pixMut.data?.brCode) && (
                        <div className="space-y-2">
                          <p className="text-xs text-slate-500 text-center">Ou copie o código abaixo:</p>
                          <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 flex items-center gap-2">
                            <code className="text-xs text-slate-700 break-all flex-1 select-all">
                              {p.pixBrCode ?? pixMut.data?.brCode}
                            </code>
                            <button
                              onClick={async () => {
                                await navigator.clipboard.writeText(p.pixBrCode ?? pixMut.data?.brCode ?? '');
                                setCopied(true);
                                setTimeout(() => setCopied(false), 2000);
                              }}
                              className="shrink-0 text-xs font-semibold text-teal-600 hover:text-teal-700"
                            >
                              {copied ? 'Copiado!' : 'Copiar'}
                            </button>
                          </div>
                        </div>
                      )}

                      {(p.pixExpiresAt || pixMut.data?.expiresAt) && (
                        <p className="text-xs text-slate-400 text-center">
                          Expira em {new Date(p.pixExpiresAt ?? pixMut.data?.expiresAt ?? '').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}

                      <button
                        onClick={() => setMode('idle')}
                        className="w-full text-sm text-slate-500 hover:text-slate-700"
                      >
                        ← Trocar método
                      </button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-xs text-amber-800">
                      <span className="mt-0.5 shrink-0">ℹ️</span>
                      <p>
                        <strong>Sessões pagas não podem ser canceladas.</strong>{' '}
                        Em caso de imprevisto, você poderá reagendar sua sessão respeitando as regras de reagendamento da plataforma.
                      </p>
                    </div>

                    <p className="text-sm text-slate-500 text-center">Escolha como deseja pagar:</p>

                    <button
                      onClick={() => cardMut.mutate()}
                      disabled={cardMut.isPending}
                      className={cn(
                        'w-full border-2 rounded-xl p-4 text-left transition hover:border-teal-400 hover:bg-teal-50',
                        'border-slate-200 bg-white',
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">💳</span>
                        <div>
                          <div className="font-semibold text-slate-800 text-sm">
                            {cardMut.isPending ? 'Preparando…' : 'Cartão de crédito'}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">Pagamento seguro</div>
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={() => pixMut.mutate()}
                      disabled={pixMut.isPending}
                      className={cn(
                        'w-full border-2 rounded-xl p-4 text-left transition hover:border-teal-400 hover:bg-teal-50',
                        'border-slate-200 bg-white',
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">⚡</span>
                        <div>
                          <div className="font-semibold text-slate-800 text-sm">
                            {pixMut.isPending ? 'Gerando QR Code…' : 'PIX'}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            Aprovação instantânea · expira em {p.schedule ? '3 dias' : '24h'}
                          </div>
                        </div>
                      </div>
                    </button>

                    {(cardMut.isError || pixMut.isError) && (
                      <p className="text-sm text-red-500 text-center">
                        Não foi possível iniciar o pagamento. Tente novamente.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {!isPaid && search.status === 'cancelled' && (
              <p className="text-sm text-slate-400 text-center mt-4">
                Pagamento cancelado. Você pode tentar novamente acima.
              </p>
            )}
          </>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string | React.ReactNode; bold?: boolean }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className={cn('text-slate-800', bold && 'font-bold text-base')}>{value}</span>
    </div>
  );
}
